import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { GMS_AUTH_LOGOUT_URL, SIGN_IN_EMAIL_URL, SIGN_OUT_URL } from '@/lib/api'
import {
  deleteMyAvatar,
  fetchMe,
  patchMe,
  uploadMyAvatar,
  type AuthUser,
  type MeResponse,
} from '@/lib/api/me'
import { GENESIS_HOME } from '@/lib/roles'
import { safeNext } from '@/lib/safe-next'
import { leavePortalView } from '@/lib/portal-view'

/**
 * Os tipos da sessão passaram para `lib/api/me.ts` na Story 15.2 — é lá que
 * vive o contrato do fio. Ficam reexportados aqui porque este era o endereço
 * deles desde a Story 1.5, e mudá-lo não acrescentaria nada a ninguém.
 */
export type { AuthUser, MeResponse, UserRole } from '@/lib/api/me'

export interface SignInEmailInput {
  email: string
  password: string
  /**
   * Story 25.1 — o destino pedido, o `?next=` do URL do login. Chega CRU: quem o
   * valida é o `safeNext`, já com o papel lido. Não viaja para o servidor — o
   * corpo do POST leva só as credenciais.
   */
  next?: string | null
}

/**
 * A frase de um sign-in barrado pelo limite por IP (429). O `/api/auth/*` conta
 * 200 pedidos por HORA por IP (Story 23.32), e um escritório inteiro atrás do
 * mesmo IP pode chegar lá: dizer «Email ou senha incorretos» a quem acertou na
 * senha mandava-o tentar outra vez, e gastar mais do mesmo balde. Não revela
 * nada sobre a conta — o 429 chega antes de o servidor olhar para ela.
 */
export const SIGN_IN_RATE_LIMITED_MESSAGE =
  'Muitas tentativas de entrada. Aguarde um pouco e tente de novo.'

/**
 * A frase de um login ACEITO cuja sessão não pegou: o POST do sign-in passou, mas
 * o `/api/me` logo a seguir respondeu sem usuário (Story 25.1, AC-6).
 *
 * Antes, esse caso navegava para `homeForRole(undefined)` — a dashboard —, cuja
 * guarda mandava de volta ao login, sem uma palavra: a tela ia e voltava sozinha.
 * Agora a pessoa fica no `/sign-in` e lê isto. Não diz que a senha está errada,
 * porque não está: o servidor acabou de a aceitar.
 */
export const SIGN_IN_SESSION_MISSING_MESSAGE =
  'Não foi possível abrir a sessão. Tente entrar de novo.'

/**
 * Posts credentials to the better-auth email sign-in endpoint
 * (`POST /api/auth/sign-in/email`). Throws on non-2xx so the caller can
 * surface an inline error; the message is intentionally generic — we never
 * leak whether the email exists or the password was wrong. The one exception is
 * a 429, which says so (`SIGN_IN_RATE_LIMITED_MESSAGE`, QA da 23.32).
 */
async function postSignInEmail(input: SignInEmailInput): Promise<void> {
  const res = await fetch(SIGN_IN_EMAIL_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email: input.email, password: input.password }),
  })
  if (res.status === 429) {
    throw new Error(SIGN_IN_RATE_LIMITED_MESSAGE)
  }
  if (!res.ok) {
    throw new Error('Email ou password incorretos')
  }
}

/**
 * A frase que o usuário vê quando o sign-out NÃO aconteceu.
 *
 * Diz duas coisas de propósito: que falhou, e que a sessão CONTINUA aberta.
 * A segunda é a que interessa num computador partilhado — sem ela, alguém
 * fecharia o portátil a pensar que tinha saído.
 */
export const SIGN_OUT_FAILED_MESSAGE =
  'Não foi possível sair. A sua sessão continua aberta — tente novamente.'

/**
 * `POST /api/auth/sign-out` — encerra a sessão no SERVIDOR.
 *
 * O CABEÇALHO É OBRIGATÓRIO, e a falta dele foi um defeito de segurança real:
 * o better-auth monta o router com `allowedMediaTypes: ['application/json']`
 * GLOBAL (better-auth 1.6.11 → better-call 1.3.5, `getBody`), aplicado a
 * qualquer pedido que traga corpo — mesmo numa rota como o `/sign-out`, que não
 * declara schema nenhum. Um `POST` do browser viaja com `Content-Length: 0`, o
 * runtime materializa um corpo vazio, e sem `Content-Type` o router responde
 * **415 Unsupported Media Type ANTES do handler**: a sessão nunca era apagada.
 *
 * Não há cliente do better-auth no `apps/web` (não é dependência) — todas estas
 * chamadas são `fetch` à mão, e esta era a única de `/api/auth/*` sem o
 * cabeçalho. O `postSignInEmail` acima sempre o teve; foi só o sign-out que
 * nasceu sem ele (Story 1.5) e ninguém reparou porque o cliente ignorava a
 * resposta.
 *
 * O corpo `{}` acompanha o cabeçalho: prometer JSON e não mandar nenhum seria
 * pedir ao servidor que fizesse `request.json()` sobre um corpo vazio.
 *
 * Lança em qualquer não-2xx. Quem trata a falha é o `onError` da mutação.
 */
async function postSignOut(): Promise<void> {
  const res = await fetch(SIGN_OUT_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    throw new Error(SIGN_OUT_FAILED_MESSAGE)
  }
}

/**
 * `POST /api/gms-auth/logout` — o «Sair» de quem entrou pela Genesis (Story 35.4,
 * Epic 35 §3). Apaga o cookie da Genesis e grava o `logout`; o sign-out do
 * better-auth não toca nesse cookie, e com ele o pedido seguinte voltava a entrar.
 *
 * A sessão de 12 horas do `auth.` continua, como no «Sair» do GMS (fora do escopo do
 * Epic 35): a próxima entrada pela Microsoft é imediata.
 *
 * Sem corpo nem `Content-Type`: a rota não lê corpo, e o 415 do better-auth (ver
 * `postSignOut`) não existe no Hono. Lança em qualquer não-2xx, como o `postSignOut`.
 */
async function postGenesisLogout(): Promise<void> {
  const res = await fetch(GMS_AUTH_LOGOUT_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(SIGN_OUT_FAILED_MESSAGE)
  }
}

/**
 * Hook that exposes the authenticated user, loading/error states, and
 * sign-in / sign-out helpers. Backed by /api/me + better-auth (Story 1.5).
 *
 * O e-mail e a senha são o caminho do cliente de fora. A conta da Genesis entra
 * pela Microsoft, pelo `gms-auth` (Epic 35): a ida é uma navegação de
 * `routes/sign-in.tsx`, e aqui fica só o «Sair» desse caminho.
 */
export function useAuth(): {
  user: AuthUser | null
  isLoading: boolean
  isError: boolean
  signInEmail: (input: SignInEmailInput) => Promise<void>
  isSigningIn: boolean
  signInError: Error | null
  resetSignInError: () => void
  /**
   * Encerra a sessão no SERVIDOR e só depois navega para `/sign-in`.
   *
   * A promessa resolve nos dois desfechos; o desfecho lê-se no ECRÃ, não no
   * valor de retorno — êxito redireciona, falha mostra um toast de erro e
   * deixa o usuário exatamente onde estava, ainda dentro. NÃO navegar a partir
   * do chamador: a navegação é do `onSuccess`, e é o que garante que a tela
   * nunca diz "saiu" sem o servidor ter confirmado.
   */
  signOut: () => Promise<void>
  /** Story 10.1 — update the current user's display name (PATCH /api/me). */
  updateProfile: (name: string) => Promise<AuthUser>
  isUpdatingProfile: boolean
  /** Story 15.2 — envia o recorte de 512×512 (POST /api/me/avatar). */
  updateAvatar: (blob: Blob, filename: string) => Promise<AuthUser>
  isUpdatingAvatar: boolean
  /** Story 15.2 — remove a foto (DELETE /api/me/avatar). */
  removeAvatar: () => Promise<void>
  isRemovingAvatar: boolean
} {
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: false,
  })

  const signInEmailMutation = useMutation({
    mutationFn: postSignInEmail,
    onSuccess: async (_data, input) => {
      // O DESTINO DEPENDE DO PAPEL (F1 do portal do cliente): um usuário de
      // cliente entra em /portal, a Genesis na dashboard. O papel só existe em
      // `/api/me`, e por isso ele é LIDO aqui antes de escolher — o corpo do
      // sign-in do better-auth não o traz. Desde a Story 25.1 o papel também
      // decide se o destino pedido (`input.next`) serve: é o `safeNext`.
      //
      // A leitura é um `fetchMe()` DIRETO, e não um `invalidateQueries` seguido
      // de `fetchQuery`: essa combinação fazia duas idas a `/api/me` por cada
      // entrada (a invalidação já refaz o pedido do observador, e o `fetchQuery`
      // com `staleTime: 0` refaz outro). A semeadura da cache faz o mesmo
      // trabalho que a invalidação fazia, com um pedido só.
      //
      // Se a leitura falhar, entra-se na casa da Genesis: é o comportamento que
      // existia antes do portal, e um cliente que aterre lá é reencaminhado pelo
      // `ProtectedRoute` assim que o `/api/me` responder. Falhar a entrada
      // inteira por causa do destino seria pior que entrar pela porta errada. O
      // `next` não entra neste caminho: sem papel lido, não há fronteira a
      // respeitar.
      let me: MeResponse
      try {
        me = await fetchMe()
      } catch {
        window.location.href = GENESIS_HOME
        return
      }
      queryClient.setQueryData(['auth', 'me'], me)

      // Ler BEM e vir sem usuário é outra coisa (Story 25.1, AC-6): a sessão não
      // pegou. Navegar para qualquer tela guardada mandava de volta ao login, e a
      // tela ia e voltava sozinha. O `throw` daqui vira o erro da mutação — o
      // `signInError` que a tela já mostra — e ninguém navega.
      if (!me.user) {
        throw new Error(SIGN_IN_SESSION_MISSING_MESSAGE)
      }
      window.location.href = safeNext(input.next, me.user.role)
    },
  })

  const signOutMutation = useMutation({
    // A sessão a encerrar é a do caminho por onde a pessoa entrou (o `login` do
    // `/api/me`). Sem `login`, é a senha: o que o «Sair» sempre fez.
    mutationFn: (login: AuthUser['login']) =>
      login === 'genesis' ? postGenesisLogout() : postSignOut(),
    // SÓ AQUI é que se navega, e só depois de o servidor confirmar. O que
    // transformou o 415 num problema de segurança não foi o 415: foi ir para
    // /sign-in sem olhar para a resposta. A navegação vive dentro do
    // `onSuccess` por isso mesmo — nenhum dos quatro sítios que chamam
    // `signOut()` navega por sua conta, e nenhum deve passar a navegar.
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], { user: null } satisfies MeResponse)
      // A vista do portal da Genesis (issue #584) não sobrevive à sessão: quem
      // entrar a seguir neste browser começa na casa dele.
      leavePortalView()
      window.location.href = '/sign-in'
    },
    // A falha é RUIDOSA e o usuário FICA ONDE ESTÁ.
    //
    // Não se limpa a cache local nem se navega: quem manda na sessão é o
    // cookie, e apagar o estado local com o cookie vivo reconstruiria
    // exatamente a mentira que este fix desfaz — a tela a dizer que saiu com a
    // sessão de pé no servidor. Continuar visivelmente dentro é o estado
    // VERDADEIRO, e é o único que leva o usuário a agir (tentar de novo,
    // fechar o browser).
    //
    // Não há retry automático (o default do React Query para mutações é 0): o
    // botão "Sair" continua no sítio e carregar nele outra vez repete o
    // pedido, que é idempotente. Um retry escondido só atrasaria o toast.
    //
    // O toast é a superfície porque `signOut()` é chamado de quatro sítios de
    // chrome sem espaço para erro inline (sidebar, portal, definições, portal
    // index) e o `<Toaster />` está na raiz do `App.tsx` — irmão do `<Router>`,
    // logo visível em TODAS as rotas, incluindo /portal.
    onError: () => {
      toast.error(SIGN_OUT_FAILED_MESSAGE)
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: patchMe,
    onSuccess: (user) => {
      // Seed the cache with the fresh user so the UI (sidebar name, settings
      // field) reflects the new name immediately, without a refetch round-trip.
      queryClient.setQueryData(['auth', 'me'], { user } satisfies MeResponse)
    },
  })

  const updateAvatarMutation = useMutation({
    mutationFn: ({ blob, filename }: { blob: Blob; filename: string }) =>
      uploadMyAvatar(blob, filename),
    onSuccess: (user) => {
      // A MESMA semeadura do nome, e pela mesma razão — só que aqui ela é
      // load-bearing: o `avatarUpdatedAt` novo é o que muda o `?v=` do URL, e
      // sem essa mudança o browser continuaria a servir a foto anterior da
      // cache. É isto que faz a sidebar mostrar a foto nova de imediato.
      queryClient.setQueryData(['auth', 'me'], { user } satisfies MeResponse)
    },
  })

  const removeAvatarMutation = useMutation({
    mutationFn: deleteMyAvatar,
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'me'], (previous: MeResponse | undefined): MeResponse => {
        // O servidor pode responder 204 (sem corpo). Nesse caso derivamos o
        // estado novo do usuário que já temos, em vez de invalidar e esperar
        // por uma ida à rede que a tela não precisa de esperar.
        if (user) return { user }
        if (!previous?.user) return { user: null }
        return { user: { ...previous.user, hasAvatar: false, avatarUpdatedAt: null } }
      })
    },
  })

  return {
    user: data?.user ?? null,
    isLoading,
    isError,
    signInEmail: async (input) => {
      await signInEmailMutation.mutateAsync(input)
    },
    isSigningIn: signInEmailMutation.isPending,
    signInError: signInEmailMutation.error,
    resetSignInError: () => {
      signInEmailMutation.reset()
    },
    signOut: async () => {
      // A promessa RESOLVE mesmo quando o sign-out falha, e isto não é
      // engolir a falha: ela já foi TRATADA no `onError` (toast) e a
      // consequência que importa — não navegar — já aconteceu (ou melhor, não
      // aconteceu). Os quatro chamadores fazem `void signOut()`; rejeitar aqui
      // só produziria uma "unhandled rejection" na consola, que é MENOS
      // visível que o toast, não mais.
      await signOutMutation.mutateAsync(data?.user?.login).catch(() => {})
    },
    updateProfile: async (name) => updateProfileMutation.mutateAsync(name),
    isUpdatingProfile: updateProfileMutation.isPending,
    updateAvatar: async (blob, filename) => updateAvatarMutation.mutateAsync({ blob, filename }),
    isUpdatingAvatar: updateAvatarMutation.isPending,
    removeAvatar: async () => {
      await removeAvatarMutation.mutateAsync()
    },
    isRemovingAvatar: removeAvatarMutation.isPending,
  }
}
